import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BanknotesIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/Button';
import { useAuth } from '@/contexts/AuthContext';
import { FormSelect } from '@/components/FormSelect';
import { FormTextarea } from '@/components/FormTextarea';
import { useToast } from '@/hooks/useToast';
import { RARO_INVEST_AMOUNT, RARO_INVEST_MIN_INVESTMENT, formatBrl, formatReferenceMonth } from '../../../../../shared/dpRequests';
import { AttachmentPicker } from '../AttachmentPicker';
import { withUploadedAttachments, type PendingFile } from '../../services/dpAttachmentUpload';
import { useSubmitRaroInvest } from '../../hooks/data/useDpRequestMutations';
import { DP_REQUESTS_PATH } from '../../routes';

interface RaroInvestFormProps {
    months: string[];
}

export function RaroInvestForm({ months }: RaroInvestFormProps) {
    const navigate = useNavigate();
    const toast = useToast();
    const { user } = useAuth();
    const submit = useSubmitRaroInvest();
    const [uploading, setUploading] = useState(false);
    const busy = submit.isPending || uploading;
    const [referenceMonth, setReferenceMonth] = useState(months.length === 1 ? months[0] : '');
    const [monthError, setMonthError] = useState<string | null>(null);
    const [files, setFiles] = useState<PendingFile[]>([]);
    const [note, setNote] = useState('');
    const [attachmentError, setAttachmentError] = useState<string | null>(null);

    async function handleSubmit() {
        if (!referenceMonth) {
            const message = 'Escolha o mês de referência';
            setMonthError(message);
            toast.error('Revise o pedido', message);
            return;
        }
        setMonthError(null);
        if (files.length === 0) {
            const message = 'Anexe a nota de negociação de títulos para enviar o pedido';
            setAttachmentError(message);
            toast.error('Revise o pedido', message);
            return;
        }
        setAttachmentError(null);
        if (!user?.uid) return;
        setUploading(true);
        try {
            await withUploadedAttachments(files, user.uid, attachments =>
                submit.mutateAsync({ referenceMonth, note: note.trim() || undefined, attachments })
            );
            navigate(DP_REQUESTS_PATH);
        } catch (error) {
            if (error instanceof Error) setMonthError(error.message);
        } finally {
            setUploading(false);
        }
    }

    return (
        <div className="space-y-6">
            <div className="flex items-start gap-4 rounded-2xl border border-primary-100 bg-primary-50 px-5 py-4">
                <BanknotesIcon className="mt-0.5 h-6 w-6 shrink-0 text-primary-600" aria-hidden="true" />
                <div className="space-y-1">
                    <p className="text-sm text-raro-gray-800">
                        Por gentileza, realizar o reembolso no valor de <strong>{formatBrl(RARO_INVEST_AMOUNT)}</strong>.
                    </p>
                    <p className="text-sm text-raro-gray-600">Referente ao benefício Raro Invest.</p>
                    <p className="text-xs text-raro-gray-500">
                        Para participar, faça um investimento mínimo de {formatBrl(RARO_INVEST_MIN_INVESTMENT)} no banco de sua preferência.
                        O reembolso é feito no mês seguinte ao da solicitação.
                    </p>
                </div>
            </div>

            <div className="space-y-5 rounded-2xl border border-raro-gray-200 bg-white p-5 shadow-sm sm:p-6">
                <FormSelect
                    label="Mês de referência"
                    name="referenceMonth"
                    value={referenceMonth}
                    onChange={event => { setReferenceMonth(event.target.value); setMonthError(null); }}
                    options={months.map(month => ({ value: month, label: formatReferenceMonth(month) }))}
                    placeholder="Selecione o mês"
                    helperText="Não é possível escolher meses futuros nem um mês que já tenha pedido."
                    error={monthError ?? undefined}
                    disabled={busy}
                    required
                />

                <AttachmentPicker
                    files={files}
                    onAdd={file => setFiles(current => [...current, file])}
                    onRemove={index => setFiles(current => current.filter((_, position) => position !== index))}
                    label="Nota de negociação de títulos"
                    hint="Obrigatório. Anexe a nota de negociação de títulos recebida do seu banco, comprovando a operação (PDF, JPG ou PNG). O arquivo só é enviado quando você enviar o pedido."
                    required
                    disabled={busy}
                />
                {attachmentError && <p className="text-xs text-rose-600" role="alert">{attachmentError}</p>}

                <FormTextarea
                    label="Observação (opcional)"
                    name="note"
                    value={note}
                    onChange={event => setNote(event.target.value)}
                    maxLength={500}
                    showCharCount
                    rows={2}
                    disabled={busy}
                />
            </div>

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button variant="ghost" onClick={() => navigate(DP_REQUESTS_PATH)} disabled={busy}>Cancelar</Button>
                <Button onClick={() => void handleSubmit()} loading={busy} disabled={!user?.uid}>{uploading ? 'Enviando anexos…' : 'Enviar ao DP'}</Button>
            </div>
        </div>
    );
}
